/**
 * Simulate open/clear product alerts while a simulation session is active.
 */
import * as React from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, Bell, Plus, X } from "lucide-react";
import { Button } from "../../../ui/components/Button";
import { Text } from "../../../ui/components/Text";
import { Callout } from "../../../ui/components/Callout";
import { Modal } from "../../../ui/components/Modal";
import type { DataCatalogAlert } from "../../lib/api/products";
import * as productsApi from "../../lib/api/products";
import { useI18n } from "../../lib/i18n";
import * as pickerStyles from "../users/AddUserToOrganizationModal.css";
import * as subStyles from "./ProductSubscriptionsModal.css";
import * as s from "./SimulationAlertControls.css";

const SEVERITY_LABELS: Record<number, string> = {
  1: "Suggestion",
  2: "Low",
  3: "Medium",
  4: "High",
  5: "Critical",
};

function severityClass(severity: number): string {
  const sev = Math.min(5, Math.max(1, severity || 1));
  if (sev <= 1) return s.iconMuted;
  if (sev === 2) return s.iconInfo;
  if (sev === 3) return s.iconWarn;
  if (sev === 4) return s.iconHigh;
  return s.iconCritical;
}

function AlertTypeIcon({
  type,
  severity,
  size = 14,
}: {
  type: string;
  severity: number;
  size?: number;
}) {
  const className = severityClass(severity);
  if (type === "fault") {
    return <AlertTriangle size={size} className={className} aria-hidden />;
  }
  return <Bell size={size} className={className} aria-hidden />;
}

function AlertOptionLabel({ alert }: { alert: DataCatalogAlert }) {
  return (
    <span className={s.optionLabel}>
      <AlertTypeIcon type={alert.type} severity={alert.severity} />
      <span className={s.optionName}>{alert.name}</span>
      <span className={s.optionCode}>{alert.code}</span>
    </span>
  );
}

export type ActiveSimAlert = {
  code: string;
  name: string;
  type: string;
  severity: number;
};

export type SimulationAlertStatusProps = {
  activeAlerts: ActiveSimAlert[];
  onClearAlert?: (code: string) => void;
  onAddAlert?: () => void;
  clearBusyCode?: string | null;
  canEdit?: boolean;
  children?: React.ReactNode;
};

function mapActiveList(
  items: Array<{ code: string; name?: string; type: string; severity?: number }>,
  catalogAlerts: DataCatalogAlert[],
): ActiveSimAlert[] {
  return items.map((i) => {
    const match = catalogAlerts.find(
      (a) => a.code.toUpperCase() === i.code.toUpperCase(),
    );
    return {
      code: match?.code ?? i.code,
      name: match?.name ?? i.name ?? i.code,
      type: match?.type ?? i.type ?? "alarm",
      severity: match?.severity ?? i.severity ?? 3,
    };
  });
}

export function SimulationAlertControls({
  token,
  productId,
  alerts,
  canEdit,
  renderStatus,
}: {
  token: string | null;
  productId: string;
  alerts: DataCatalogAlert[];
  canEdit: boolean;
  renderStatus: (props: SimulationAlertStatusProps) => React.ReactNode;
}) {
  const { t } = useI18n();
  const [selectedCode, setSelectedCode] = React.useState<string>("");
  const [activeAlerts, setActiveAlerts] = React.useState<ActiveSimAlert[]>([]);
  const [modalOpen, setModalOpen] = React.useState(false);
  const [search, setSearch] = React.useState("");
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [dropdownPosition, setDropdownPosition] = React.useState<{
    top: number;
    left: number;
    width: number;
  } | null>(null);
  const [busy, setBusy] = React.useState(false);
  const [clearBusyCode, setClearBusyCode] = React.useState<string | null>(null);
  const [error, setError] = React.useState<string | null>(null);

  const triggerRef = React.useRef<HTMLDivElement>(null);
  const dropdownRef = React.useRef<HTMLDivElement>(null);
  const searchInputRef = React.useRef<HTMLInputElement>(null);

  const sortedAlerts = React.useMemo(
    () =>
      [...alerts]
        .filter((a) => a.isActive)
        .sort(
          (a, b) =>
            a.sortOrder - b.sortOrder ||
            a.code.localeCompare(b.code) ||
            a.name.localeCompare(b.name),
        ),
    [alerts],
  );

  const activeCodes = React.useMemo(
    () => new Set(activeAlerts.map((a) => a.code.toUpperCase())),
    [activeAlerts],
  );

  const availableAlerts = React.useMemo(
    () => sortedAlerts.filter((a) => !activeCodes.has(a.code.toUpperCase())),
    [sortedAlerts, activeCodes],
  );

  React.useEffect(() => {
    if (!token) return;
    let cancelled = false;
    productsApi
      .getProductSimulationState(token, productId)
      .then((state) => {
        if (cancelled) return;
        const raw =
          state.activeAlerts ??
          (state.activeAlert ? [state.activeAlert] : []);
        setActiveAlerts(mapActiveList(raw, alerts));
      })
      .catch(() => {
        /* ignore — overlay is ephemeral */
      });
    return () => {
      cancelled = true;
    };
  }, [token, productId, alerts]);

  const selected = availableAlerts.find((a) => a.code === selectedCode) ?? null;
  const canOpen = Boolean(selectedCode) && canEdit && !busy;
  const dropdownVisible = modalOpen && pickerOpen && canEdit;

  const filteredAlerts = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return availableAlerts;
    return availableAlerts.filter(
      (a) =>
        a.code.toLowerCase().includes(q) ||
        a.name.toLowerCase().includes(q) ||
        (a.description ?? "").toLowerCase().includes(q) ||
        (SEVERITY_LABELS[a.severity] ?? "").toLowerCase().includes(q),
    );
  }, [availableAlerts, search]);

  React.useLayoutEffect(() => {
    if (!dropdownVisible || !triggerRef.current) {
      setDropdownPosition(null);
      return;
    }
    const rect = triggerRef.current.getBoundingClientRect();
    setDropdownPosition({
      top: rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, [dropdownVisible, search, filteredAlerts.length]);

  React.useEffect(() => {
    if (!dropdownVisible) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t) || dropdownRef.current?.contains(t)) return;
      setPickerOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setPickerOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [dropdownVisible]);

  const closeModal = () => {
    setModalOpen(false);
    setPickerOpen(false);
    setSearch("");
    setSelectedCode("");
    setError(null);
  };

  const handleOpen = async () => {
    if (!token || !selectedCode || !canOpen) return;
    setBusy(true);
    setError(null);
    try {
      const res = await productsApi.openProductSimulationAlert(
        token,
        productId,
        selectedCode,
      );
      setActiveAlerts(mapActiveList(res.activeAlerts ?? [], alerts));
      closeModal();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to open alert");
    } finally {
      setBusy(false);
    }
  };

  const handleClear = async (code: string) => {
    if (!token || !canEdit) return;
    setClearBusyCode(code);
    setError(null);
    try {
      const res = await productsApi.clearProductSimulationAlert(
        token,
        productId,
        code,
      );
      setActiveAlerts(mapActiveList(res.activeAlerts ?? [], alerts));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to clear alert");
    } finally {
      setClearBusyCode(null);
    }
  };

  return (
    <>
      {renderStatus({
        activeAlerts,
        onClearAlert: canEdit ? handleClear : undefined,
        onAddAlert:
          canEdit && availableAlerts.length > 0
            ? () => {
                setError(null);
                setSelectedCode("");
                setSearch("");
                setModalOpen(true);
              }
            : undefined,
        clearBusyCode,
        canEdit,
      })}

      {error && !modalOpen ? (
        <div style={{ marginTop: 8 }}>
          <Callout variant="error">{error}</Callout>
        </div>
      ) : null}

      <Modal
        open={modalOpen}
        setOpen={(open) => {
          if (!open) closeModal();
          else setModalOpen(true);
        }}
        minWidth="420px"
        maxWidth="480px"
        minHeight="auto"
        closeOnClickOff
      >
        <Modal.Header>
          <Text level="h3" weight="bold">
            Simulate alert
          </Text>
        </Modal.Header>
        <Modal.Body className={s.modalBodyCompact}>
          {error ? <Callout variant="error">{error}</Callout> : null}
          {availableAlerts.length === 0 ? (
            <Text level="body-small" className={s.mutedText}>
              All catalog alerts are already active.
            </Text>
          ) : (
            <div className={s.pickerCol}>
              {selected ? (
                <div className={s.modalComboTrigger}>
                  <span className={s.selectedChip}>
                    <AlertOptionLabel alert={selected} />
                    <button
                      type="button"
                      className={subStyles.chipClear}
                      aria-label={t("shared.listView.clearSelection")}
                      disabled={busy}
                      onClick={() => {
                        setSelectedCode("");
                        setSearch("");
                        setPickerOpen(true);
                        requestAnimationFrame(() => searchInputRef.current?.focus());
                      }}
                    >
                      <X size={12} />
                    </button>
                  </span>
                </div>
              ) : (
                <div
                  ref={triggerRef}
                  className={s.modalComboTrigger}
                  onClick={() => {
                    if (busy) return;
                    setPickerOpen(true);
                    searchInputRef.current?.focus();
                  }}
                >
                  <input
                    ref={searchInputRef}
                    className={subStyles.comboInput}
                    value={search}
                    onChange={(e) => {
                      setSearch(e.target.value);
                      setPickerOpen(true);
                    }}
                    onFocus={() => setPickerOpen(true)}
                    placeholder={t("pro.products.alerts.searchByNameOrCode")}
                    disabled={busy}
                  />
                </div>
              )}

              {dropdownVisible &&
                dropdownPosition &&
                createPortal(
                  <div
                    ref={dropdownRef}
                    className={pickerStyles.pickerDropdown}
                    style={{
                      top: dropdownPosition.top,
                      left: dropdownPosition.left,
                      width: dropdownPosition.width,
                    }}
                    role="listbox"
                  >
                    {filteredAlerts.length === 0 ? (
                      <div className={s.emptyOption}>
                        <Text level="body-small" className={s.mutedText}>
                          No alerts match “{search.trim()}”
                        </Text>
                      </div>
                    ) : (
                      filteredAlerts.map((alert) => (
                        <button
                          key={alert.id}
                          type="button"
                          role="option"
                          aria-selected={selectedCode === alert.code}
                          className={pickerStyles.pickerOption}
                          data-selected={selectedCode === alert.code ? "true" : "false"}
                          title={`${alert.name} (${alert.code}) · ${SEVERITY_LABELS[alert.severity] ?? alert.severity}`}
                          onClick={() => {
                            setSelectedCode(alert.code);
                            setPickerOpen(false);
                            setSearch("");
                          }}
                        >
                          <AlertOptionLabel alert={alert} />
                        </button>
                      ))
                    )}
                  </div>,
                  document.body,
                )}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" color="neutral" onClick={closeModal} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="button"
            color="primary"
            onClick={handleOpen}
            loading={busy}
            disabled={!canOpen || availableAlerts.length === 0}
          >
            Simulate Open
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
